import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SEO from '@/components/feature/SEO';
import { banners, categories, hotMovies, newMovies, topRated, movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  const prevBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextBanner]);

  const currentMovie = banners[currentBanner];

  const handleBannerInteraction = () => setIsAutoPlaying(false);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '成全影院 - 免费电影在线观看',
    description: '成全影院为您提供免费电影在线观看，海量电影大全电视剧全集，综艺大全最新影片，高清不卡顿免费看。每日更新热门影视资源，成全您的观影体验。',
    url: 'https://chengquan.tv/',
    isPartOf: {
      '@type': 'WebSite',
      name: '成全影院',
      url: 'https://chengquan.tv/',
    },
  };

  const randomMovies: Movie[] = [...movies].sort(() => Math.random() - 0.5).slice(0, 8);

  return (
    <>
      <SEO
        title="首页"
        description="成全影院为您提供免费电影在线观看，海量电影大全电视剧全集，综艺大全最新影片，高清不卡顿免费看。每日更新热门影视资源，成全您的观影体验。"
        keywords="成全影院,免费电影,在线观看,电视剧全集,综艺大全,最新影片,电影大全,高清,不卡顿,免费观看"
        canonicalUrl="https://chengquan.tv/"
        schema={homeSchema}
      />

      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main>
          {/* Hero Banner */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="精选推荐">
            {banners.map((banner, index) => (
              <div
                key={banner.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentBanner ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                <img
                  src={banner.banner}
                  alt={banner.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-950"></div>
              </div>
            ))}

            <div className="absolute inset-0 flex items-center">
              <div className="max-w-[1400px] mx-auto w-full px-4 md:px-6">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-primary-500 text-background-50 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      正在热播
                    </span>
                    <span className="text-foreground-300 text-sm">{currentMovie.year} / {currentMovie.subcategory}</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-50 mb-3 leading-tight">
                    {currentMovie.title}
                  </h2>
                  <p className="text-foreground-300 text-sm md:text-base leading-relaxed mb-4 line-clamp-2 max-w-lg">
                    {currentMovie.description}
                  </p>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400"></i>
                      <span className="text-accent-400 font-bold text-lg">{currentMovie.rating}</span>
                    </div>
                    <span className="text-foreground-400 text-sm">{currentMovie.duration}</span>
                    <span className="text-foreground-400 text-sm">{currentMovie.views.toLocaleString()} 次观看</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/article/${currentMovie.id}`}
                      className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      立即观看
                    </Link>
                    <Link
                      to={`/category/${currentMovie.category}`}
                      className="inline-flex items-center gap-2 bg-background-800/70 hover:bg-background-700/70 text-foreground-200 px-5 py-3 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
                    >
                      更多{currentMovie.category === 'movie' ? '电影' : currentMovie.category === 'tv' ? '电视剧' : currentMovie.category === 'variety' ? '综艺' : '动漫'}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => { prevBanner(); handleBannerInteraction(); }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-950/40 hover:bg-background-950/60 text-foreground-50 transition-all cursor-pointer backdrop-blur-sm"
              aria-label="上一个"
            >
              <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
            </button>
            <button
              onClick={() => { nextBanner(); handleBannerInteraction(); }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-950/40 hover:bg-background-950/60 text-foreground-50 transition-all cursor-pointer backdrop-blur-sm"
              aria-label="下一个"
            >
              <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => { setCurrentBanner(index); handleBannerInteraction(); }}
                  className={`h-1 rounded-full transition-all cursor-pointer ${
                    index === currentBanner
                      ? 'w-6 bg-primary-500'
                      : 'w-2 bg-foreground-400/40 hover:bg-foreground-400/60'
                  }`}
                  aria-label={`切换到第${index + 1}张`}
                />
              ))}
            </div>
          </section>

          {/* Categories */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10" aria-label="视频分类">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50">视频分类</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="group relative rounded-xl overflow-hidden h-[100px] md:h-[130px] bg-background-800 hover:bg-background-700 transition-all cursor-pointer"
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                      <i className={`${cat.icon} text-2xl md:text-3xl text-primary-400 group-hover:text-primary-300 transition-colors`}></i>
                    </div>
                    <span className="text-sm md:text-base font-medium text-foreground-200 group-hover:text-foreground-50 transition-colors">
                      {cat.name}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Hot Movies */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10" aria-label="热门推荐">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50">热门推荐</h3>
                <span className="text-xs text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full">TOP 8</span>
              </div>
              <Link
                to="/category/movie"
                className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} size="medium" />
              ))}
            </div>
          </section>

          {/* New Movies */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10" aria-label="最新上线">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50">最新上线</h3>
                <span className="text-xs text-accent-400 bg-accent-500/10 px-2 py-0.5 rounded-full">NEW</span>
              </div>
              <Link
                to="/category/movie"
                className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {newMovies.slice(0, 6).map((movie) => (
                <MovieCard key={movie.id} movie={movie} size="medium" />
              ))}
            </div>
          </section>

          {/* Top Rated */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10" aria-label="高分推荐">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50">高分推荐</h3>
              </div>
              <Link
                to="/category/movie"
                className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {topRated.map((movie, index) => (
                <div key={movie.id} className="relative">
                  <MovieCard movie={movie} size="medium" />
                  <div className="absolute top-0 left-0 w-7 h-7 flex items-center justify-center bg-accent-500 text-background-950 text-xs font-extrabold rounded-br-lg">
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Picks For You */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-14" aria-label="猜你喜欢">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50">猜你喜欢</h3>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {randomMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} size="medium" />
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14" aria-label="常见问题">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 text-center mb-8">
                常见问题
              </h3>
              <div className="space-y-3">
                {[
                  { q: '成全影院的视频需要付费吗？', a: '完全不需要！成全影院所有视频内容均为免费在线观看，无需注册，无需付费，打开即可观看。' },
                  { q: '视频画质怎么样？会卡顿吗？', a: '我们提供高清画质视频，采用先进的CDN加速技术，确保观影过程流畅不卡顿。建议在网络良好的环境下观看以获得最佳体验。' },
                  { q: '支持哪些设备观看？', a: '成全影院支持电脑、平板、手机等多种设备观看，自适应各种屏幕尺寸，随时随地享受观影乐趣。' },
                  { q: '影片多久更新一次？', a: '我们每日更新最新影视资源，热门电影、电视剧、综艺、动漫同步上线，确保您不会错过任何精彩内容。' },
                  { q: '可以下载视频离线观看吗？', a: '目前成全影院为在线观看平台，暂不支持视频下载功能。所有视频均为在线流媒体播放。' },
                ].map((faq, index) => (
                  <details key={index} className="group bg-background-900/50 rounded-xl border border-background-800/50 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm md:text-base font-medium text-foreground-200 group-open:text-primary-400 transition-colors">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-400 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>

              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: [
                      { '@type': 'Question', name: '成全影院的视频需要付费吗？', acceptedAnswer: { '@type': 'Answer', text: '完全不需要！成全影院所有视频内容均为免费在线观看，无需注册，无需付费，打开即可观看。' } },
                      { '@type': 'Question', name: '视频画质怎么样？会卡顿吗？', acceptedAnswer: { '@type': 'Answer', text: '我们提供高清画质视频，采用先进的CDN加速技术，确保观影过程流畅不卡顿。建议在网络良好的环境下观看以获得最佳体验。' } },
                      { '@type': 'Question', name: '支持哪些设备观看？', acceptedAnswer: { '@type': 'Answer', text: '成全影院支持电脑、平板、手机等多种设备观看，自适应各种屏幕尺寸，随时随地享受观影乐趣。' } },
                      { '@type': 'Question', name: '影片多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '我们每日更新最新影视资源，热门电影、电视剧、综艺、动漫同步上线，确保您不会错过任何精彩内容。' } },
                      { '@type': 'Question', name: '可以下载视频离线观看吗？', acceptedAnswer: { '@type': 'Answer', text: '目前成全影院为在线观看平台，暂不支持视频下载功能。所有视频均为在线流媒体播放。' } },
                    ],
                  }),
                }}
              />
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}